import React, { useEffect, useMemo, useState } from "react";
import Row from "@amzn/meridian/row";
import Column from "@amzn/meridian/column";
import Text from "@amzn/meridian/text";
import Box from "@amzn/meridian/box";
import Button from "@amzn/meridian/button";
import { FILTER_CONFIGS, FilterValues } from "./filterOptions";
import { ApproachType, useGetExpandedCompsQuery } from "../../../endpoints/getExpandedComps";
import LoaderBox from "../../loader/LoaderBox";
import { useUpdateAppealMutation } from "../../../endpoints/updateAppeal";
import { useAppSelector } from "../../../app/store/hooks";
import { LoadingIndicator } from "./ApiLoadingIndicator";
import { StatusEnum } from "../../../endpoints/getArtifacts";
import { useUpdateExpandedCompsMutation } from "../../../endpoints/updateExpandedComps";
import { useSheets } from "../../../features/sheets/hooks";
import { PropertyDetailsSheet } from "./PropertyDetailsSheet";
import {
    filterProperties,
    getSubjectIdentifier,
    getSubjectProperty,
    updateCompsInData,
} from "./expandedCompsUtils";
import styles from "./style.module.scss";
import { createHandlers } from "./expanded-comps-sheet-components/handlers";
import { SearchAndFilters } from "./expanded-comps-sheet-components/SearchAndFilters";
import { PropertyList } from "./expanded-comps-sheet-components/PropertyList";
import { getUpdatedExpandedComps, PendingChanges } from "./expanded-comps-sheet-components/utils";
import { ImageSheet } from "../commons/ImageSheet";
import Icon from "@amzn/meridian/icon";
import closeSmallTokens from "@amzn/meridian-tokens/base/icon/close-small";

interface ExpandedCompsSheetProps {
    viewType: ApproachType;
    data: any;
    spaceId?: string;
    closeSheet: () => void;
    refetch: () => void;
    handleUpdatedata?: any;
}

export const ExpandedCompsSheet: React.FC<ExpandedCompsSheetProps> = ({
    viewType,
    data,
    spaceId,
    closeSheet,
    refetch,
}) => {
    const { alias } = useAppSelector(state => state.auth.user);
    const [searchQuery, setSearchQuery] = useState("");
    const [isSearching, setIsSearching] = useState(false);
    const [isFetching, setIsFetching] = useState(false);
    const [showAddedOnly, setShowAddedOnly] = useState(true);
    const [displayCount, setDisplayCount] = useState(9);
    const [allComps, setAllComps] = useState<any[]>([]);
    const { pushToStack, getCurrentStack, setOpenSheets, closeSheetFromStack } = useSheets();
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
    const [updateAppeal, { isError: appealError, error: appealErrorMsg }] =
        useUpdateAppealMutation();
    const [updateExpandedComps, { isLoading: isUpdating }] = useUpdateExpandedCompsMutation();

    const [filterValues, setFilterValues] = useState<FilterValues>(() => {
        return FILTER_CONFIGS.reduce(
            (acc, config) => ({
                ...acc,
                [config.key]: config.key === "orderBy" ? config.defaultValue : "All",
            }),
            {}
        ) as FilterValues;
    });

    const [pendingChanges, setPendingChanges] = useState<PendingChanges>({
        added: new Set(),
        removed: new Set(),
    });

    const handlers = createHandlers(
        setSearchQuery,
        setIsSearching,
        setDisplayCount,
        setPendingChanges,
        setAllComps,
        setShowAddedOnly,
        setFilterValues
    );

    useEffect(() => {
        if (searchQuery !== "") {
            setIsSearching(true);
            const timer = setTimeout(() => {
                setDebouncedSearchQuery(searchQuery);
                setIsSearching(false);
            }, 300);

            return () => clearTimeout(timer);
        } else {
            setDebouncedSearchQuery("");
            setIsSearching(false);
        }
    }, [searchQuery]);

    const subjectIdentifier = useMemo(
        () => getSubjectIdentifier(viewType, data.appealDetails.propertyNumber, spaceId),
        [viewType, data.appealDetails.propertyNumber, spaceId]
    );

    const queryParams = useMemo(
        () => ({
            businessUseCase: "SMART",
            appealArtifactId: data.appealArtifactId,
            approachType: viewType,
            subjectIdentifier: subjectIdentifier,
        }),
        [viewType, subjectIdentifier, data.appealArtifactId]
    );

    const {
        data: expandedCompsData,
        isLoading,
        isError,
        error,
        refetch: expandedCompsDataRefetch,
    } = useGetExpandedCompsQuery(queryParams);

    useEffect(() => {
        if (expandedCompsData) {
            setAllComps(expandedCompsData.expandedCompsDetails);
        }
    }, [expandedCompsData]);
    const [displayImage, setDisplayImage] = useState(false);
    const [selectedProperty, setSelectedProperty] = useState<any>(null);

    const handleImageDisplay = (property: any, imageStatus: boolean) => {
        setSelectedProperty(property);
        setDisplayImage(imageStatus);
        closeSheetFromStack("propertyDetails");
    };

    const handleSave = async () => {
        try {
            const updatedData = JSON.parse(JSON.stringify(data));
            updateCompsInData(updatedData, viewType, spaceId, pendingChanges, allComps);
            setIsFetching(true);
            await saveChangesToServer(updatedData);
            await refreshData();
            closeSheet();
            setPendingChanges({ added: new Set(), removed: new Set() });
        } catch (error) {
            console.error("Error saving changes:", error);
        } finally {
            setIsFetching(false);
        }
    };

    const saveChangesToServer = async (updatedData: any) => {
        const [updateAppealResponse] = await Promise.all([
            updateAppeal({
                businessUseCase: "SMART",
                appealArtifactId: data.appealArtifactId,
                appealDetails: updatedData.appealDetails,
                appealStatus: StatusEnum.PREPARING,
                clientName: alias,
            }),
            updateExpandedComps({
                businessUseCase: "SMART",
                appealArtifactId: data.appealArtifactId,
                expandedCompsDetails: getUpdatedExpandedComps(allComps, pendingChanges),
                approachType: viewType,
                subjectIdentifier: subjectIdentifier,
            }),
        ]);

        if ("error" in updateAppealResponse) {
            throw new Error("Failed to update appeal");
        }
    };

    const refreshData = async () => {
        await expandedCompsDataRefetch();
        await refetch();
    };

    const handleViewDetails = (property: any) => {
        const currentStack = getCurrentStack();
        const propertyDetailsSheet = (
            <PropertyDetailsSheet
                property={property}
                handleImageDisplay={status => handleImageDisplay(property, status)}
            />
        );
        const propertyDetailsExists = currentStack.some(sheet => sheet.id === "propertyDetails");

        if (!propertyDetailsExists) {
            pushToStack(propertyDetailsSheet, "800px", "propertyDetails");
        }
        setOpenSheets(true);
    };

    const subjectProperty = useMemo(
        () => getSubjectProperty(data, viewType, spaceId),
        [data, viewType, spaceId]
    );

    const filteredProperties = useMemo(
        () =>
            filterProperties(
                allComps,
                debouncedSearchQuery,
                showAddedOnly,
                filterValues,
                subjectProperty,
                viewType
            ),
        [allComps, debouncedSearchQuery, showAddedOnly, filterValues, subjectProperty, viewType]
    );

    const displayedProperties = filteredProperties.slice(0, displayCount);

    const shouldShowLoadMore = useMemo(() => {
        return filteredProperties.length > 9 && displayCount < filteredProperties.length;
    }, [filteredProperties.length, displayCount]);

    if (isError || appealError) {
        return (
            <LoadingIndicator isError={true} error={error || appealErrorMsg} isLoading={false} />
        );
    }

    return (
        <>
            <Column className={styles.sheetContainer}>
                <Box width="71rem" className={styles.contentArea}>
                    <Column spacing="400" width="100%">
                        <Row alignmentHorizontal={"justify"}>
                            <Text type="h400">
                                Edit {viewType === ApproachType.SALES ? "sales" : "rental"} comps
                            </Text>
                            <Button
                                onClick={() => {
                                    closeSheetFromStack("expandedComps");
                                }}
                                type="icon"
                            >
                                <Icon tokens={closeSmallTokens}>Close sheet</Icon>
                            </Button>
                        </Row>

                        <Box width="71rem">
                            <Text type="b400">Search comp</Text>
                        </Box>

                        <SearchAndFilters
                            searchQuery={searchQuery}
                            handleSearch={handlers.handleSearch}
                            handleSearchSubmit={handlers.handleSearchSubmit}
                            showAddedOnly={showAddedOnly}
                            handleToggleChange={handlers.handleToggleChange}
                            filterValues={filterValues}
                            handleFilterChange={handlers.handleFilterChange}
                        />

                        {isSearching || isFetching ? (
                            <LoaderBox
                                loadingText={isSearching ? "Searching..." : "Updating..."}
                                height="fit"
                            />
                        ) : (
                            <>
                                <PropertyList
                                    displayedProperties={displayedProperties}
                                    viewType={viewType}
                                    handleAdd={handlers.handleAdd}
                                    handleRemove={handlers.handleRemove}
                                    handleViewDetails={handleViewDetails}
                                />

                                {!isLoading && shouldShowLoadMore && (
                                    <Box spacingInset="0 0">
                                        <Button onClick={handlers.handleLoadMore} type="secondary">
                                            Load more
                                        </Button>
                                    </Box>
                                )}
                            </>
                        )}

                        {(isLoading || isUpdating) && (
                            <Box spacingInset="300 500">
                                <Text>Loading...</Text>
                            </Box>
                        )}
                    </Column>
                </Box>
                <Box className={styles.buttonContainer} spacingInset={"300"}>
                    <Row spacingInset="400" alignmentHorizontal="end">
                        <Button type="primary" onClick={handleSave}>
                            Save
                        </Button>
                    </Row>
                </Box>
            </Column>
            {selectedProperty && (
                <ImageSheet
                    imageHeader="Property Images"
                    s3ObjectIdList={selectedProperty.pdfPageS3Keys}
                    displayImage={displayImage}
                    handleDisplayImage={status => handleImageDisplay(selectedProperty, status)}
                    index={0}
                />
            )}
        </>
    );
};
